import React, { useEffect, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Callout, DataListItem } from '@node-space/ui-components'
import { useRewardsSheetContext } from 'pages/Rewards/context/RewardsSheetProvider'
import useRewardsTracking from 'pages/Rewards/hooks/useRewardsTracking'
import { formatAmount, prepareWithdrawReviewData } from 'pages/Rewards/utils'
import { useWithdrawCalculations } from '../../hooks/useWithdrawCalculations'
import { useWithdrawFormState } from '../../hooks/useWithdrawFormState'
import { useWithdrawMutations } from '../../hooks/useWithdrawMutations'
import { useWithdrawSheetHeaderUpdates } from '../../hooks/useWithdrawSheetHeaderUpdates'
import { RewardsReview } from './RewardsReview'
import { RewardsWithdrawActions } from './RewardsWithdrawActions'
import { RewardsWithdrawFormFields } from './RewardsWithdrawFormFields'

export type WithdrawMode = 'standard' | 'removeAllocation'
type WithdrawStep = 'form' | 'review'

interface RewardsWithdrawFormProps {
  withdrawType: WithdrawMode
}

export const RewardsWithdraw = ({ withdrawType }: RewardsWithdrawFormProps) => {
  const { trackFormInitiate, trackFormCancel } = useRewardsTracking()
  const { t } = useTranslation()
  const {
    rewardsSheetAction,
    handleSheetUpdate,
    isCustomerRewards,
    selectedCurrency: portfolioCurrency,
    userPortfolios,
    rewardsProducts,
    customerEarningsSummary,
  } = useRewardsSheetContext()

  const isRemoveAllocation = withdrawType === 'removeAllocation'
  const [currentStep, setCurrentStep] = useState<WithdrawStep>('form')

  const {
    formMethods,
    selectedCurrency,
    selectedWallet,
    enteredAmount,
    withdrawSource,
    maxAmount,
    setMaxAmount,
  } = useWithdrawFormState({
    portfolioCurrency,
    customerEarningsTotalUnpaid: customerEarningsSummary?.totalUnpaidEarnings,
    userPortfolios,
    rewardsProducts,
  })

  const { blockedBalance, remainingAmount } = useWithdrawCalculations({
    selectedCurrency,
    userPortfolios,
    enteredAmount,
    isRemoveAllocation,
  })

  const { onWithdraw, isWithdrawPending, withdrawRequestError, setWithdrawRequestError } =
    useWithdrawMutations({
      isRemoveAllocation,
      userPortfolios,
      selectedCurrency,
      selectedWallet,
      onClose: rewardsSheetAction?.onClose,
    })

  useEffect(() => {
    trackFormInitiate(isRemoveAllocation ? 'removeAllocation' : 'withdraw')
  }, [isRemoveAllocation, trackFormInitiate])

  const onReviewBack = () => {
    setCurrentStep('form')
  }

  const onCancel = () => {
    trackFormCancel(isRemoveAllocation ? 'removeAllocation' : 'withdraw')
    rewardsSheetAction?.onClose?.()
  }

  const onSubmit = () => {
    setWithdrawRequestError(null)
    setCurrentStep('review')
  }

  const onConfirmWithdraw = (twoFactorCode?: string) => {
    const formData = formMethods.getValues()
    onWithdraw({
      formData,
      withdrawSource,
      twoFactorCode,
    })
  }

  useWithdrawSheetHeaderUpdates({
    currentStep,
    isRemoveAllocation,
    handleSheetUpdate,
    onReviewBack,
    rewardsSheetAction,
  })

  if (currentStep === 'review') {
    const require2FA = formMethods.getValues('withdrawSource') === 'customerRewards'
    return (
      <RewardsReview
        data={prepareWithdrawReviewData(formMethods.getValues(), selectedCurrency, {
          isRemoveAllocation,
          blockedBalance,
        })}
        requestError={withdrawRequestError}
        isPending={isWithdrawPending}
        require2FA={require2FA}
        onCancel={onCancel}
        onConfirm={onConfirmWithdraw}
        resetRequestError={() => setWithdrawRequestError(null)}
      />
    )
  }

  return (
    <FormProvider {...formMethods}>
      <Box
        tag="form"
        flexCol
        gapY={16}
        width="full"
        paddingY={24}
        onSubmit={formMethods.handleSubmit(onSubmit)}
      >
        {withdrawRequestError?.show && (
          <Callout variant="error" testId="withdraw-error-callout">
            {withdrawRequestError?.handledMessage}
          </Callout>
        )}

        {isRemoveAllocation && (
          <DataListItem
            id="amount-pending-allocation"
            testId="amount-pending-allocation"
            title={t('rewards.removeAllocation.amountPendingAllocation')}
            body={`${formatAmount(Number.parseFloat(blockedBalance))} ${selectedCurrency}`}
          />
        )}

        <RewardsWithdrawFormFields
          isRemoveAllocation={isRemoveAllocation}
          isCustomerRewards={isCustomerRewards}
          isPending={isWithdrawPending}
          selectedCurrency={selectedCurrency}
          portfolioCurrency={portfolioCurrency}
          rewardsProducts={rewardsProducts}
          withdrawSource={withdrawSource}
          maxAmount={maxAmount}
          onMaxAmountChange={setMaxAmount}
        />

        {isRemoveAllocation && (
          <DataListItem
            id="amount-remaining"
            testId="amount-remaining"
            title={t('rewards.removeAllocation.amountRemaining')}
            body={`${formatAmount(remainingAmount)} ${selectedCurrency}`}
          />
        )}

        <RewardsWithdrawActions
          onCancel={onCancel}
          isPending={isWithdrawPending}
          selectedCurrency={selectedCurrency}
        />
      </Box>
    </FormProvider>
  )
}
